import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import cn from 'clsx'
import { rem } from 'polished'
import moment from 'moment'
import Clickable from 'components/Common/Clickable'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { pluralizeToString } from 'lib/pluralize'

import Heading from 'components/Luxkit/Typography/Heading'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import TextLink from 'components/Luxkit/TextLink'
import { getOnlyNRoomLeftLabel } from 'lib/getUrgencyLabels'
import UrgencyLabel from 'components/Luxkit/Label/UrgencyLabel'
import PaletteProvider from 'contexts/PaletteProvider'
import Group from 'components/utils/Group'
import SolidUsdCircleIcon from 'components/Luxkit/Icons/solid/SolidUsdCircleIcon'
import LineFireIcon from 'components/Luxkit/Icons/line/LineFireIcon'
import SolidFireIcon from 'components/Luxkit/Icons/solid/SolidFireV2Icon'
import { isLPCTacticalEnabled } from 'lib/offerUtils'
import { Trans, useTranslation } from 'react-i18next'

const HeaderGroup = styled(Group)`
  position: relative;
  z-index: 1;
`

const CellWrapper = styled(Clickable)`
  --transition-timing: 0.2s;

  position: relative;
  display: flex;
  flex-direction: column;
  padding: ${rem(4)} ${rem(8)};
  transition: background-color var(--transition-timing);
  min-height: ${rem(72)};

  box-shadow: 0 -1px 0 0 ${props => props.theme.palette.neutral.five},
  1px 0 0 0 ${props => props.theme.palette.neutral.five},
  1px 0 0 0 ${props => props.theme.palette.neutral.five} inset,
  0 -1px 0 0 ${props => props.theme.palette.neutral.five} inset;

  &:not(.check-in):not(.check-out):not(:disabled):not(.other-availability):not(.is-complementary) {
    &:focus, &:hover {
      background-color: ${props => props.theme.palette.highlight.primary.light};
    }
  }

  &:disabled, &.unselectable {
    background-color: ${props => props.theme.palette.neutral.seven};
  }

  &.check-in, &.check-out {
    background-color: ${props => props.theme.palette.highlight.primary.normal};
  }

  &.between-day {
    background-color: ${props => props.theme.palette.highlight.primary.light};
  }

  &.is-bundle {
    justify-content: space-between;
  }

  &.sold-out-background {
    background-color: ${props => props.theme.palette.neutral.seven};
  }

  &.is-complementary {
    --polygon-width: ${rem(16)};
    --polygon-base: 0;
    background-color: ${props => props.theme.palette.primary.normal};

     /* The Bundle & Save Polygon */
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 1px;
      width: 100%;
      height: 100%;
      z-index: 0;
      clip-path: polygon(0 0, calc(var(--polygon-base) + var(--polygon-width)) 0, var(--polygon-base) 100%, 0 100%);
      background-color: ${props => props.theme.palette.neutral.one};
      transform-origin: center;
    }

    &.complementary-check-in {
      position: relative;
      background-color: ${props => props.theme.palette.neutral.eight};

      &::before {
        right: 0;
        transform: scaleX(-1) scaleY(-1);
      }

      &.check-out {
        background-color: ${props => props.theme.palette.highlight.primary.normal};
      }
    }

    &.complementary-check-out {
      --polygon-base: 20%;
      position: relative;
      background-color: ${props => props.theme.palette.neutral.eight};

      &::before {
        transform: scaleX(1);
      }

      &.check-in {
        background-color: ${props => props.theme.palette.highlight.primary.normal};
      }
    }
  }
`

const PropertyNameContainer = styled.div`
  position: relative;
  z-index: 1;
  height: 100%;
`

const Main = styled.div`
  position: relative;
  opacity: 1;
  pointer-events: auto;
  transition: opacity 0.2s;
  display: flex;
  flex-direction: column;

  &.hidden {
    opacity: 0;
    pointer-events: none;
  }

  &.align-items-end {
    align-items: end;
  }
`

const BundleAndSaveAdditionalAmount = styled.div`
  text-align: right;
`

interface Props {
  isCheckIn?: boolean;
  isCheckOut?: boolean;
  duration: number;
  day: App.CalendarDay;
  hasOtherAvailability: boolean;
  style?: React.CSSProperties;
  isRoomSelected?: boolean;
  className?: string;
  currentOfferId?: string;
  bundleOfferId?: string;
  flights?: boolean;
  isBetweenDay?: boolean;
  isComplementaryBundleCell?: boolean
  isComplementaryCheckIn?: boolean
  isComplementaryCheckOut?: boolean
  complementaryPropertyName?: string
  isIllegalBundleSelection?: boolean
  checkIn: string
  flightsOnly?: boolean;
  enableCalendarRecommendations: boolean;
  hasPromo?: boolean;
  isTacticalPromotion?: boolean;
  onClick?: (day: App.CalendarDay) => void;
  cheapestDay?: App.CalendarCheapestDay;
  onInvalidBundleDateClick: () => void
}

function OfferCalendarDayCell(props: Props) {
  const {
    duration,
    day,
    hasOtherAvailability,
    isCheckIn,
    isCheckOut,
    style,
    isRoomSelected,
    className,
    flights,
    isBetweenDay,
    flightsOnly,
    enableCalendarRecommendations,
    currentOfferId,
    bundleOfferId,
    isComplementaryBundleCell = false,
    isComplementaryCheckIn = false,
    isComplementaryCheckOut = false,
    isIllegalBundleSelection = false,
    complementaryPropertyName,
    onClick,
    hasPromo = false,
    isTacticalPromotion = false,
    cheapestDay,
    onInvalidBundleDateClick,
  } = props

  const dispatch = useOfferPageDispatch()

  const { t } = useTranslation(['common', 'offerPage'])

  const isFromPrice = !isRoomSelected
  const price = flights ? day.packagedTotal : day.hotelTotal
  const flightPriceAvailable = day.flightPrice > 0
  const available = day.hotelAvailable && (!flights || day.flightsAvailable) && (flightsOnly ? day.flightsAvailable : true)
  const canSwitchToHotel = day.hotelAvailable && flights && !flightsOnly
  const colour = (isCheckIn || isCheckOut) ? 'neutral-one' : undefined

  const availability = useMemo(() => {
    const result = {
      inOtherRoomTypes: false,
      showRecommendations: false,
      available: false,
      soldOut: false,
      canSwitchHotel: false,
    }
    if (available) {
      result.available = true
    } else if (!available) {
      if (canSwitchToHotel) {
        result.canSwitchHotel = true
      } else if (hasOtherAvailability) {
        result.inOtherRoomTypes = true
      } else {
        if (enableCalendarRecommendations) {
          result.showRecommendations = true
        } else {
          result.soldOut = true
        }
      }
    }
    return result
  }, [available, canSwitchToHotel, hasOtherAvailability, enableCalendarRecommendations])

  const isInvalidBundleDate = !isComplementaryCheckOut && isIllegalBundleSelection

  const display = useMemo(() => {
    const cellSelection = {
      isBetweenDay,
      isCheckOut,
      isCheckIn,
      isNone: !isBetweenDay && !isCheckIn && !isCheckOut,
    }

    return {
      cellSelection,
      shouldDisableButton: availability.soldOut || (availability.showRecommendations && !cellSelection.isNone),
      isUnselectable: isInvalidBundleDate,
      shouldHideMainInformation: cellSelection.isBetweenDay || cellSelection.isCheckOut || (isComplementaryBundleCell && !isComplementaryCheckOut),
      showSwitchToViewHotel: availability.canSwitchHotel,
      showAvailableInOtherRoomTypes: availability.inOtherRoomTypes,
      showSoldOut: availability.showRecommendations || availability.soldOut,
      showRecommendationLink: availability.showRecommendations && cellSelection.isNone && !bundleOfferId,
      showSoldOutBackground: availability.showRecommendations && cellSelection.isNone,
      showPrices: availability.available && (!flightsOnly || (flightsOnly && day.flightCacheActive)),
      showUrgencyLabel: isRoomSelected && !isBetweenDay && !isCheckIn && !isCheckOut,
      isBundleSelection: !!bundleOfferId,
      complementaryCell: isComplementaryBundleCell,
      complementaryCheckIn: isComplementaryCheckIn,
      complementaryCheckOut: isComplementaryCheckOut,
      shouldWhitenDay: isComplementaryCheckIn || isComplementaryCheckOut,
      complementaryCheckInIntersect: isComplementaryCheckIn && isCheckOut,
      complementaryCheckOutIntersect: isComplementaryCheckOut && isCheckIn,
      inversePallete: isComplementaryBundleCell && !(isComplementaryCheckIn || isComplementaryCheckOut),
      showPropertyName: isComplementaryCheckIn,
    }
  }, [availability, isBetweenDay, isCheckIn, isCheckOut, isRoomSelected, flightsOnly, day.flightCacheActive, isComplementaryBundleCell, isComplementaryCheckIn, isComplementaryCheckOut, bundleOfferId, isInvalidBundleDate])

  const roomsLeftLabel = useMemo(() => {
    return !display.showSoldOut && display.showUrgencyLabel && !(display.complementaryCell && !display.complementaryCheckOut) ? getOnlyNRoomLeftLabel(day.availableRooms, true) : undefined
  }, [day, display.showUrgencyLabel, display.showSoldOut, display.complementaryCell, display.complementaryCheckOut])

  const onCellClick = useCallback(() => {
    if (isCheckIn) { return }
    if (isInvalidBundleDate) {
      onInvalidBundleDateClick()
      return
    }
    if (availability.canSwitchHotel) {
      dispatch({ type: OfferPageActions.setFlights, flights: false })
    } else if (availability.inOtherRoomTypes) {
      dispatch({ type: OfferPageActions.toggleSubFilter, filter: 'room' })
      dispatch({ type: OfferPageActions.setCheckInDate, checkIn: moment(day.checkIn), offerId: currentOfferId })
    } else if (availability.showRecommendations) {
      dispatch({ type: OfferPageActions.showRecommendationsView, checkInDay: day.checkIn })
    } else {
      dispatch({ type: OfferPageActions.setCheckInDate, checkIn: moment(day.checkIn), offerId: currentOfferId })
    }
    onClick?.(day)
  }, [isCheckIn, isInvalidBundleDate, availability.canSwitchHotel, availability.inOtherRoomTypes, availability.showRecommendations, onClick, day, onInvalidBundleDateClick, dispatch, currentOfferId])

  const bundleAdditionalAmount = useMemo(() => {
    if (cheapestDay) {
      const diff = day.hotelTotal - cheapestDay.hotelTotal
      if (diff >= 1) {
        return diff
      }
    }
    if (day.surcharge > 0) {
      return day.surcharge
    }
    return null
  }, [day, cheapestDay])

  let FireIcon
  if (isLPCTacticalEnabled()) {
    FireIcon = <SolidFireIcon size="XS" colour={!colour ? 'urgency' : undefined} />
  } else {
    FireIcon = <LineFireIcon size="XS" colour={!colour ? 'secondary-normal' : undefined} />
  }
  const isPriceWithFlights = !!(flights && flightPriceAvailable)
  const showFrom = !!(isFromPrice || (flights && !flightPriceAvailable))
  const priceDescriptionKey = `calendar.nNights${isPriceWithFlights ? 'PlusFlights' : ''}${showFrom ? 'From' : ''}` as const

  return (
    <CellWrapper
      style={style}
      data-testid={cn({
        'available-day-cell': availability.available,
      })}
      className={cn(className, {
        unselectable: display.showAvailableInOtherRoomTypes || display.isUnselectable,
        'check-in': display.cellSelection.isCheckIn,
        'check-out': display.cellSelection.isCheckOut,
        'between-day': display.cellSelection.isBetweenDay,
        'sold-out-background': display.showSoldOutBackground,
        'is-complementary': display.complementaryCell,
        'complementary-check-in': display.complementaryCheckIn,
        'complementary-check-out': display.complementaryCheckOut,
        'is-bundle': display.isBundleSelection,
      })}
      disabled={display.shouldDisableButton}
      onClick={onCellClick}
    >
      <PaletteProvider type={display.inversePallete ? 'inverse' : 'default'}>
        <HeaderGroup fullWidth direction="horizontal" verticalAlign="center" horizontalAlign="space-between" data-testid={cn({ [`d-${day.checkIn}`]: availability.available })}>
          <Heading colour={display.shouldWhitenDay ? 'neutral-eight' : colour} as="span" variant="heading6">{day.day}</Heading>
          <Group direction="horizontal" verticalAlign="center" gap={4}>
            {(display.cellSelection.isCheckIn || display.cellSelection.isCheckOut) && <BodyText colour="neutral-one" as="span" variant="small" weight="semi-bold">
              {display.cellSelection.isCheckIn && t('Check in', { ns: 'common' })}
              {display.cellSelection.isCheckOut && t('Check out', { ns: 'common' })}
            </BodyText>}
            {roomsLeftLabel && <UrgencyLabel variant="critical1">{roomsLeftLabel}</UrgencyLabel>}
            {hasPromo && !display.cellSelection.isBetweenDay && <SolidUsdCircleIcon size="XS" colour={!colour ? 'secondary-normal' : undefined} />}
            {!hasPromo && display.cellSelection.isNone && isTacticalPromotion && FireIcon}
          </Group>
        </HeaderGroup>

        {display.shouldHideMainInformation && display.showPropertyName && complementaryPropertyName &&
          <PropertyNameContainer>
            <BodyText colour="neutral-eight" lineClamp={2} underline variant="small" weight="bold">{complementaryPropertyName}</BodyText>
          </PropertyNameContainer>}

        {!display.shouldHideMainInformation && <Main data-testid="d-main" className={cn({ 'align-items-end': display.isBundleSelection })}>
          {display.showPrices && <>
            {bundleOfferId && (
              <BundleAndSaveAdditionalAmount data-testid="day-price">
                <BodyText colour={colour} variant="small" weight="normal">
                  {pluralizeToString('night', duration)}
                </BodyText>
                {bundleAdditionalAmount && (
                  <BodyText colour={colour} variant="small" weight="normal">
                    from +<FormatCurrency value={bundleAdditionalAmount} /> *
                  </BodyText>
                )}
              </BundleAndSaveAdditionalAmount>
            )}
            {!bundleOfferId && (<>
              <Caption colour={colour ?? 'neutral-three'} variant="medium">
                {t(priceDescriptionKey, { ns: 'offerPage', count: duration })}
              </Caption>
              <div data-testid="day-price">
                <BodyText colour={hasPromo ? 'highlight' : colour} as="span" variant="medium" weight="bold">
                  <FormatCurrency value={price} />
                </BodyText>
                <Caption colour={colour} as="span" variant="medium"> {t('total', { ns: 'common' })}{flights && '*'}</Caption>
                {flights && !flightPriceAvailable && <Caption colour={colour} as="span" variant="medium"> {t('plusFlights', { ns: 'offerPage' })}</Caption>}
              </div>
            </>)}
          </>}

          {display.showSwitchToViewHotel && <>
            <Caption variant="large" colour={colour ?? 'neutral-three'}>{t('Flights not available', { ns: 'offerPage' })}</Caption>
            <TextLink size="small">{t('View hotel prices', { ns: 'offerPage' })}</TextLink>
          </>}

          {display.showSoldOut && <Caption variant="large" colour={colour ?? 'neutral-three'}>{t('SOLD OUT', { ns: 'offerPage' })}</Caption>}
          {display.showRecommendationLink && <TextLink size="small">{t('See more great offers', { ns: 'offerPage' })}</TextLink>}

          {display.showAvailableInOtherRoomTypes && (<Trans i18nKey="availableInOtherRoomTypesPartialLink" ns="offerPage">
            <Caption variant="large" colour="neutral-three">Available in other</Caption>
            <TextLink size="small">room types</TextLink>
          </Trans>)}
        </Main>}
      </PaletteProvider>
    </CellWrapper>
  )
}

export default React.memo(OfferCalendarDayCell)
